<template>
  <div class="fu-patient" data-test="fu-cell-patient">
    <span class="fu-patient__avatar" aria-hidden="true">{{ item.initials }}</span>
    <div class="fu-patient__text">
      <div class="fu-patient__line">
        <fu-icon v-if="opening" name="spin" :size="16" :stroke="2.4" class="fu-patient__spin" label="Opening the follow-up panel" data-test="fu-opening" />
        <span
          v-if="error"
          v-b-tooltip.hover.focus
          class="fu-patient__error"
          tabindex="0"
          role="img"
          :title="errorText"
          :aria-label="errorText"
          data-test="fu-open-error"
        >
          <fu-icon name="alert" :size="16" />
        </span>
        <button type="button" class="fu-patient__name" data-test="fu-open-drawer" @click="$emit('open')">
          {{ item.name || 'Unnamed patient' }}
        </button>
        <template v-if="showList && item.list">
          <router-link
            v-if="item.listRoute"
            v-b-tooltip.hover.focus
            :to="item.listRoute"
            class="fu-patient__list"
            :title="listText"
            :aria-label="listText"
            data-test="fu-list-badge"
          >
            <fu-icon name="list" :size="14" />
          </router-link>
          <span
            v-else
            v-b-tooltip.hover.focus
            class="fu-patient__list"
            tabindex="0"
            role="img"
            :title="listText"
            :aria-label="listText"
            data-test="fu-list-badge"
          >
            <fu-icon name="list" :size="14" />
          </span>
        </template>
        <button
          v-if="showAi"
          type="button"
          class="fu-patient__ai"
          :disabled="!item.aiCallable || aiCalling"
          :title="aiText"
          :aria-label="aiText"
          data-test="fu-ai-call"
          @click.stop="$emit('ai-call')"
        >
          <fu-icon v-if="aiCalling" name="spin" :size="14" :stroke="2.2" class="fu-patient__spin" />
          <span v-else class="fu-patient__ai-text" aria-hidden="true">AI</span>
        </button>
      </div>
      <div v-if="item.sub" class="fu-patient__sub">{{ item.sub }}</div>
    </div>
  </div>
</template>

<script>
/**
 * The first column: avatar, the name (a real button — it opens today's follow-up drawer), the
 * spinner / error mark legacy shows beside the name while the drawer opens, the "on a list" badge,
 * the Super-Admin AI call button (recall queues) and the sub-line (age · provider …, rowModel.js).
 *
 * The initials come from the name AS RECEIVED — with the demo mask on, the data boundary has
 * already replaced it, so the avatar always matches the name. Nothing is masked here.
 */
import FuIcon from './FuIcon.vue';

export default {
  name: 'FollowupsPatientCell',
  components: { FuIcon },
  props: {
    item: { type: Object, required: true },   // rowModel view-model + sub / listRoute / aiCallable
    opening: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    showAi: { type: Boolean, default: false },
    aiCalling: { type: Boolean, default: false },
    showList: { type: Boolean, default: true },
  },
  computed: {
    errorText() {
      return 'Something went wrong while trying to make a follow-up attempt. Please try again in a moment.';
    },
    listText() {
      const name = this.item.list && this.item.list.name;
      return name ? `Belongs to list - ${name}` : 'Belongs to a list';
    },
    aiText() {
      return this.item.aiCallable
        ? 'Trigger AI call (simulation)'
        : 'No mobile number or texting is off in Open Dental';
    },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-patient {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
}

.fu-patient__avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: $fu-brand-100;
  color: $fu-brand-700;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: $fu-fw-bold;
  font-size: 12px;
  flex: none;
}

.fu-patient__text {
  min-width: 0;
}

.fu-patient__line {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.fu-patient__name {
  @include fu-button-reset;
  @include fu-ellipsis;
  min-width: 0;
  max-width: 100%;
  border-radius: $fu-radius-xs;
  font-weight: $fu-fw-bold;
  font-size: 14px;
  color: $fu-link;
  &:hover { text-decoration: underline; }
  @include fu-focus-ring;
}

.fu-patient__spin {
  flex: none;
  color: $fu-brand;
  @include fu-spin;
}

.fu-patient__error {
  display: inline-flex;
  flex: none;
  color: $fu-danger;
  border-radius: 50%;
  @include fu-focus-ring;
}

.fu-patient__list {
  display: inline-flex;
  flex: none;
  color: $fu-brand-500;
  border-radius: $fu-radius-xs;
  &:hover { color: $fu-brand-700; }
  @include fu-focus-ring;
}

// The Super-Admin AI call button. It read as an empty rounded box: a 15px bot (itself a rounded
// box) drawn in #c8cfcd when disabled, inside a near-white border. Now it says "AI" — teal on a
// teal tint when it can call, muted grey on a dashed grey box when it can't (the title says why).
// 26px wide, so the name keeps its room. The spinner replaces the text while a call starts.
.fu-patient__ai {
  @include fu-icon-btn(24px);
  width: 26px;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.03em;
  line-height: 1;
  border: 1px solid $fu-brand-200;
  border-radius: 7px;
  background: $fu-brand-50;
  color: $fu-brand-700;
  &:hover:not([disabled]) { background: $fu-brand-100; color: $fu-brand-800; }
  &[disabled] {
    border-style: dashed;
    border-color: $fu-border-strong;
    background: $fu-neutral-50;
    color: $fu-text-muted;
    opacity: 1;
  }
}

.fu-patient__sub {
  @include fu-ellipsis;
  font-size: 12px;
  color: $fu-text-muted;
  margin-top: 2px;
}
</style>
